import React, { PropsWithChildren, useEffect, useRef } from "react";

type ButtonProps = {
  onClick: () => void;
  className?: string;
  children?: React.ReactNode;
};

export const Button: React.FC<{
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded ${className}`}
  >
    {children}
  </button>
);

export const Dialog: React.FC<PropsWithChildren &{
  modalOpen: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  closeModal?: () => void;
  className?: string;
  CancelButton?: React.FC
  ConfirmButton?: React.FC
}> = ({
  modalOpen,
  onCancel,
  onConfirm,
  closeModal,
  className = `m-auto border border-gray-100 rounded-sm shadow-sm p-4 w-fit h-fit max-w-[90vw] max-h-[85vh] select-none`,
  children = <></>,
  CancelButton = (props: ButtonProps) => <Button {...props} className='bg-gray-200 hover:bg-gray-300'>Cancel</Button>,
  ConfirmButton = (props: ButtonProps) => <Button {...props} className='text-white bg-blue-500 hover:bg-blue-600'>Confirm</Button>,
}) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (modalOpen) {
      ref.current?.showModal();
    } else { 
      ref.current?.close();
    }
  }, [modalOpen]);

  const handleClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) {
      onCancel?.();
      closeModal?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
    closeModal?.();
  };

  const handleConfirm = () => {
    onConfirm?.();
    closeModal?.();
  };

  return (
    <dialog
      ref={ref}
      onCancel={handleCancel}
      onClick={handleClick}
      className={className}
    >
      {children}
      <div className="flex justify-center mt-4 space-x-4">
        <CancelButton onClick={handleCancel} />
        <ConfirmButton onClick={handleConfirm} />
      </div>
    </dialog>
  );
};

export function useDialog(open: boolean = false) {
  const [isDialogOpen, setModalOpen] = React.useState(open);
  const openDialog = () => setModalOpen(true);
  const closeDialog = () => setModalOpen(false);

  return {
    isDialogOpen,
    openDialog,
    closeDialog,
    Dialog: ({children = <></>, ...props}) => <Dialog modalOpen={isDialogOpen} closeModal={closeDialog} {...props}>{children}</Dialog>
  };

}

export default Dialog;
